import React, { useState } from "react";
import Sidenav from "../../common/Sidenav";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import {
    Button,
    InputLabel,
    Typography,
    TextField,
    Grid,
    Card,
    Divider,
    Snackbar,
    Alert,
    useMediaQuery,
} from "@mui/material";

import Header from "../../common/Header";
import NavigatedComponent from "../NavigatedComponent";
import { useLocation, useNavigate } from "react-router-dom";
import { DropzoneArea } from "mui-file-dropzone";
import { apiList, invokeApi } from "../../../services/apiServices";
import { config } from "../../../config/config";
import { useCookies } from "react-cookie";
import IconSidenav from "../../common/IconSidenav";

const AddFilm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [cookies] = useCookies();
    const [filmImage, setFilmImage] = useState(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const isMobileScreen = useMediaQuery(
        "(min-width:360px) and (max-width:500px)"
    );

    const [film, setFilm] = useState({
        title: "",
        url: "",
        imageUrl: "",
    });

    const [showSideNav, setShowSideNav] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const toggleSideNav = () => {
        setShowSideNav(!showSideNav);
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFilm((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleFilmImage = (files) => {
        if (files && files.length > 0) {
            setFilmImage(files[0]);
        } else {
            setFilmImage(null);
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setOpenSnackbar(true);
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    const uploadFilmImage = async () => {
        if (!filmImage) {
            showSnackbar("Please select an image first", "warning");
            return "";
        }

        const formData = new FormData();
        formData.append("image", filmImage);

        try {
            const response = await fetch("https://api.reef.org.in/uploadFilmImage", {
                method: "POST",
                body: formData,
            });

            if (response.ok) {
                const data = await response.json();
                return data.imageUrl || "";
            } else {
                showSnackbar("Failed to upload image", "error");
                return "";
            }
        } catch (error) {
            showSnackbar("Something went wrong with image upload", "error");
            return "";
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting) return;

        if (!film.title || !film.url) {
            showSnackbar("Please fill all required fields", "warning");
            return;
        }

        if (!filmImage) {
            showSnackbar("Please upload a film poster", "warning");
            return;
        }

        setIsSubmitting(true);

        const imageUrl = await uploadFilmImage();

        if (!imageUrl) {
            setIsSubmitting(false);
            return;
        }

        const params = {
            ...film,
            image: imageUrl, // Mapping imageUrl to 'image' or 'imageUrl' as per Clint.js usage preference, likely backend expects 'image' or 'imageUrl'. keeping both in mind or sticking to one. Clint.js checks both. I'll send 'image' and 'imageUrl' ? No, just 'imageUrl' to match my state, but Clint.js checks film.imageUrl || film.image. Let's send `imageUrl`.
            imageUrl: imageUrl,
        };

        try {
            const response = await invokeApi(
                config.getMyCollege + apiList.addFilm,
                params,
                cookies
            );

            if (response?.status >= 200 && response?.status < 300) {
                showSnackbar("Film added successfully", "success");
                navigate("/films-manager/films-list");
            } else {
                showSnackbar(response.data?.message || "Failed to add film", "error");
            }
        } catch (error) {
            console.error("Error adding film:", error);
            showSnackbar("Something went wrong. Please try again later!!", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Grid sx={{ display: "flex" }}>
                {isMobileScreen ? (
                    <IconSidenav />
                ) : showSideNav ? (
                    <IconSidenav />
                ) : (
                    <Sidenav />
                )}
                <Grid component="main" sx={{ width: "100%", px: 1 }}>
                    <Header toggleSideNav={toggleSideNav} />
                    <Grid sx={{ py: 1 }}>
                        <NavigatedComponent pathname={location.pathname} />
                        <Grid sx={{ width: "100%", px: isMobileScreen ? 1 : 5 }}>
                            <Card elevation={1} sx={{ my: 5 }}>
                                <Grid sx={{ py: 2 }}>
                                    <Typography
                                        sx={{
                                            mb: 2,
                                            mx: isMobileScreen ? 2 : 8,
                                            fontSize: isMobileScreen ? 14 : 18,
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        Add New Film
                                    </Typography>
                                    <Divider sx={{ mb: 3 }} />

                                    <form onSubmit={handleSubmit}>
                                        <Grid container sx={{ justifyContent: "center" }}>

                                            <Grid item md={11} xs={11} sx={{ my: 2 }}>
                                                <InputLabel sx={{ fontWeight: "bold", my: 1, fontSize: 15 }}>
                                                    Film Title *
                                                </InputLabel>
                                                <TextField
                                                    placeholder="Enter Film Title"
                                                    fullWidth
                                                    name="title"
                                                    value={film.title}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </Grid>

                                            <Grid item md={11} xs={11} sx={{ my: 2 }}>
                                                <InputLabel sx={{ fontWeight: "bold", my: 1, fontSize: 15 }}>
                                                    URL *
                                                </InputLabel>
                                                <TextField
                                                    placeholder="Enter URL (Drive/YouTube)"
                                                    fullWidth
                                                    name="url"
                                                    value={film.url}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </Grid>

                                            <Grid item md={11} xs={11} sx={{ my: 2 }}>
                                                <InputLabel sx={{ fontWeight: "bold", my: 1, fontSize: 15 }}>
                                                    Poster Image *
                                                </InputLabel>
                                                <DropzoneArea
                                                    filesLimit={1}
                                                    onChange={handleFilmImage}
                                                    acceptedFiles={["image/jpeg", "image/png"]}
                                                    maxFileSize={5000000}
                                                    showPreviews={true}
                                                    showPreviewsInDropzone={false}
                                                    Icon={() => (
                                                        <CloudUploadIcon sx={{ color: "#1d1a57", width: 35, height: 35 }} />
                                                    )}
                                                    dropzoneText={
                                                        <Typography sx={{ color: "#1d1a57", fontWeight: 600 }}>
                                                            Upload / Drag image here
                                                        </Typography>
                                                    }
                                                />
                                            </Grid>

                                        </Grid>

                                        <Divider sx={{ mt: 4 }} />
                                        <Grid container sx={{ justifyContent: "center" }}>
                                            <Button
                                                type="submit"
                                                disabled={isSubmitting}
                                                sx={{
                                                    my: 2,
                                                    px: 3,
                                                    background: "#1d1a57",
                                                    color: "#FFF",
                                                    "&:hover": { background: "#1d1a57" },
                                                    "&:disabled": { background: "#ccc" },
                                                }}
                                            >
                                                <DriveFolderUploadIcon /> &nbsp;
                                                {isSubmitting ? "Publishing..." : "Publish"}
                                            </Button>
                                        </Grid>
                                    </form>
                                </Grid>
                            </Card>

                            <Snackbar
                                open={openSnackbar}
                                autoHideDuration={2000}
                                onClose={handleCloseSnackbar}
                                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                            >
                                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
                                    {snackbarMessage}
                                </Alert>
                            </Snackbar>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

export default AddFilm;
